import { useState, useEffect, useCallback } from 'react';
import { MessageCircle, Check, AlertCircle, Loader2, ExternalLink, ChevronDown, ChevronUp } from '../../icons/wp-icons';
import { whatsappApi } from '../../api/whatsappApi';

const SETUP_STEPS = [
  {
    title: 'Meta Business-Konto erstellen',
    description: 'Falls Sie noch keins haben, erstellen Sie ein kostenloses Meta Business-Konto.',
    link: 'https://business.facebook.com/overview',
    linkText: 'Meta Business Suite öffnen'
  },
  {
    title: 'WhatsApp-Konto hinzufügen',
    description: 'In der Meta Business Suite unter "Konten" → "WhatsApp-Konten" ein neues WhatsApp Business-Konto anlegen und Ihre Geschäftsnummer verifizieren.',
    link: 'https://business.facebook.com/settings/whatsapp-business-accounts',
    linkText: 'WhatsApp-Konten öffnen'
  },
  {
    title: 'Telefonnummer-ID kopieren',
    description: 'Unter dem WhatsApp-Konto → "Telefonnummern" finden Sie die Telefonnummer-ID (eine lange Zahl, z.B. 123456789012345). Kopieren Sie diese.',
  },
  {
    title: 'Hier einfügen und verbinden',
    description: 'Fügen Sie die ID unten ein und klicken Sie auf "Verbinden".',
  }
];

function WhatsAppAgentStep({ onDataChange }) {
  const [phoneNumberId, setPhoneNumberId] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [connected, setConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [showSteps, setShowSteps] = useState(true);

  // Prüfen ob bereits verbunden
  const checkExisting = useCallback(async () => {
    try {
      const settings = await whatsappApi.getSettings();
      if (settings.metaPhoneNumberId && settings.metaConnected) {
        setConnected(true);
        setConnectionInfo({
          displayName: settings.metaDisplayName,
          phoneNumber: settings.phoneNumber,
          qualityRating: settings.metaQualityRating
        });
      }
    } catch (err) {
      // Noch nicht konfiguriert
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkExisting();
    onDataChange({ skipped: false }, true);
  }, []);

  useEffect(() => {
    onDataChange({ whatsappConnected: connected }, true);
  }, [connected]);

  const handleConnect = async () => {
    if (!phoneNumberId.trim()) {
      setError('Bitte geben Sie die Telefonnummer-ID ein.');
      return;
    }

    setConnecting(true);
    setError('');

    try {
      const result = await whatsappApi.connectMeta(phoneNumberId.trim());
      if (result.success) {
        setConnected(true);
        setConnectionInfo(result.phoneInfo);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      if (msg.includes('verifiziert') || msg.includes('verified')) {
        setError('Die Telefonnummer-ID konnte nicht gefunden werden. Bitte prüfen Sie die ID und stellen Sie sicher, dass die Nummer bei Meta verifiziert ist.');
      } else {
        setError('Verbindung fehlgeschlagen: ' + msg);
      }
    } finally {
      setConnecting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#378ab2]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          WhatsApp-Assistent einrichten
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Kunden können Ihnen per WhatsApp schreiben und Ihr KI-Assistent antwortet automatisch
          {' '}&ndash; rund um die Uhr, auch wenn Sie gerade auf der Baustelle sind.
        </p>
      </div>

      {connected ? (
        /* Erfolgs-Anzeige */
        <div className="bg-green-50 border border-green-200 rounded-xl p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center shadow-sm">
              <MessageCircle className="w-8 h-8 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h4 className="text-lg font-medium text-green-900">
                  WhatsApp verbunden
                </h4>
                <Check className="w-5 h-5 text-green-600" />
              </div>
              {connectionInfo?.displayName && (
                <p className="text-green-700 text-sm">
                  {connectionInfo.displayName}
                  {connectionInfo.phoneNumber && ` (${connectionInfo.phoneNumber})`}
                </p>
              )}
            </div>
          </div>
          <p className="text-green-700 text-sm">
            Ihr WhatsApp-Assistent ist bereit. Kunden können Ihnen jetzt per WhatsApp schreiben
            und bekommen automatisch eine Antwort.
          </p>
        </div>
      ) : (
        <>
          {/* Schritt-für-Schritt Anleitung */}
          <div className="bg-gray-50 rounded-xl overflow-hidden">
            <button
              onClick={() => setShowSteps(!showSteps)}
              className="w-full flex items-center justify-between p-4 hover:bg-gray-100 transition"
            >
              <h4 className="font-medium text-gray-900 flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-[#25D366]" />
                Anleitung: WhatsApp Business einrichten
              </h4>
              {showSteps ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>

            {showSteps && (
              <div className="px-4 pb-4 space-y-3">
                {SETUP_STEPS.map((step, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-[#378ab2] text-white flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{step.title}</div>
                      <p className="text-sm text-gray-600 mt-0.5">{step.description}</p>
                      {step.link && (
                        <a
                          href={step.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-sm text-[#378ab2] hover:underline mt-1"
                        >
                          {step.linkText} <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Eingabefeld */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Telefonnummer-ID aus Meta Business Suite
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={phoneNumberId}
                onChange={(e) => { setPhoneNumberId(e.target.value); setError(''); }}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2] focus:border-transparent text-lg"
                placeholder="z.B. 123456789012345"
              />
              <button
                onClick={handleConnect}
                disabled={connecting || !phoneNumberId.trim()}
                className="px-6 py-3 bg-[#378ab2] text-white rounded-lg hover:bg-[#2a6d8a] transition font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {connecting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Prüfe...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Verbinden
                  </>
                )}
              </button>
            </div>

            {error && (
              <div className="mt-3 flex items-start gap-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          {/* Überspringen Option */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <strong>Noch kein WhatsApp Business?</strong> Kein Problem &ndash; dieser Schritt ist optional.
              Sie können WhatsApp jederzeit später in den Einstellungen einrichten. Klicken Sie einfach auf "Weiter".
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default WhatsAppAgentStep;
